import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import {
  IStationSwitcherContext,
  StationSwitcherContext,
} from '@components/providers/station/context';
import { Fragment, useContext } from 'react';
import { LoadingButton } from '@mui/lab';
import { FileSelector } from '@components/file-selector.tsx';
import { LoadingDialog } from '@sections/configure-plan-stack/loading-dialog.tsx';
import {
  sizes_enums,
  useConfigurePlanStack,
} from './use-configure-plan-stack';
import VersionMultiSelector from '@components/version-multi-selector.tsx';
import { vehicles_joins } from '@helpers/constants.tsx';
import { IVehiclesCapacities } from '@helpers/types';
import RouteTimeSelector from '@components/route-time-selector.tsx';
import {
  Check,
  CloudDownload,
  Error,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { MetricsTable } from '@sections/configure-plan-stack/metrics-table.tsx';
import Breadcrumb from '@/components/breadcrumb';

export const ConfigurePlanStack = () => {
  const { destStation: station } = useContext<IStationSwitcherContext>(
    StationSwitcherContext,
  );
  const hooks = useConfigurePlanStack(station);

  if (!station) {
    return <LoadingDialog message="Cargando estación..." />;
  }

  return (
    <>
      <Breadcrumb
        sections={[
          { label: 'Simulaciones', link: '/' },
          { label: 'Configuración Masiva' },
        ]}
        description="Configuración de múltiples simulaciones"
      />
      <LoadingDialog message={hooks.loadingMessage} />
      <Container sx={{ padding: 2 }}>
        <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
          <Paper variant="outlined" sx={{ padding: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" display={'inline'}>
                  Planificador de rutas{' - '}
                </Typography>
                <Typography fontWeight="bold" display={'inline'} fontSize={23}>
                  {station.station}
                  <sup>{station.country}</sup>
                </Typography>
              </Grid>
              <Grid container spacing={1} size={{ xs: 6 }}>
                <Grid size={{ xs: 12 }}>
                  <FileSelector
                    value={hooks.file}
                    types={['xlsx']}
                    onChangeFile={hooks.onChangeFile}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <div className="flex justify-end">
                    <Button
                      target="_blank"
                      component={'a'}
                      variant="outlined"
                      href={'/template.xlsx'}
                    >
                      Plantilla
                    </Button>
                  </div>
                </Grid>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <VersionMultiSelector
                  country={station.country}
                  station={station.station}
                  value={hooks.versions}
                  onChange={hooks.onChangeVersion}
                />
                {hooks.error && (
                <Alert color="warning" icon={<Error />} sx={{mt:1}}>
                  {hooks.error}
                </Alert>
              )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              {/* Tabla de configuraciones */}
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>
                        Versión
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>
                        Nombre del plan
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }}>
                        Prefijo
                      </TableCell>
                      {sizes_enums.map((size) => (
                        <TableCell
                          sx={{ fontWeight: 'bold', width: 60 }}
                          align="center"
                          key={`title_size_${size}`}
                        >
                          {size.toString()}
                        </TableCell>
                      ))}

                      {vehicles_joins.map((x) => (
                        <TableCell
                          sx={{ fontWeight: 'bold', width: 90 }}
                          align="center"
                          key={`title_vehicle_${x.value}`}
                        >
                          <Stack alignItems="center" spacing={0}>
                            {x.icon}
                            <Typography variant="caption">Unidades</Typography>
                          </Stack>
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 'bold', width: 150 }}>
                        Tiempo de ruta
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(hooks.configurations).map((key) => (
                      <Fragment key={key}>
                        <TableRow
                          sx={{
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <IconButton
                                size="small"
                                onClick={() => hooks.onToggleExpand(key)}
                                disabled={hooks.configurations[key].touched}
                              >
                                {hooks.expanded === key ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>
                              <Typography variant="body2">
                                {hooks.versions_dic[key]?.version || 'No disponible'}
                                {hooks.configurations[key]?.plan_id && (
                                  <>
                                    <Check fontSize="inherit" color="primary" />
                                    [{hooks.configurations[key]?.metrics?.total ?? 0}]
                                  </>
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="outlined"
                              fullWidth
                              size="small"
                              autoComplete="off"
                              value={hooks.configurations[key].plan_name}
                              onChange={({ target: { value } }) =>
                                hooks.onCngPlanName(key, value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              autoComplete="off"
                              value={hooks.configurations[key].prefix}
                              onChange={({ target: { value } }) =>
                                hooks.onCngPrefix(key, value)
                              }
                            />
                          </TableCell>
                          {sizes_enums.map((size) => (
                            <TableCell align="center" key={`value_size_${size}`}>
                              <Checkbox
                                checked={
                                  hooks.configurations[key]?.sizes?.includes(size) ??
                                  false
                                }
                                onChange={() => hooks.onCngSizes(key, size)}
                              />
                            </TableCell>
                          ))}

                          {vehicles_joins.map((x) => (
                            <TableCell key={`value_vehicle_${x.value}`} align="center">
                              <TextField
                                fullWidth
                                size="small"
                                value={
                                  hooks.configurations[key]?.vehicles?.[
                                    x.value as keyof IVehiclesCapacities
                                  ] ?? 0
                                }
                                onChange={({ target: { value: val } }) => {
                                  let v: string = val.replace(/[^0-9]/g, '');
                                  if (v.length === 0) {
                                    v = '0';
                                  }
                                  hooks.onChgVehicles(key, x.value, parseInt(v));
                                }}
                                inputProps={{
                                  inputMode: 'numeric',
                                  pattern: '[0-9]*',
                                }}
                              />
                            </TableCell>
                          ))}
                          <TableCell>
                            <RouteTimeSelector
                              value={hooks.configurations[key]?.routeTime ?? 0}
                              onChange={(value) => hooks.onChgRouteTime(key, value)}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={13}
                            sx={{ py: 0, borderBottom: hooks.expanded === key ? 1 : 0 }}
                          >
                            <Collapse in={hooks.expanded === key} timeout="auto">
                              <Box sx={{ py: 2 }}>
                                <MetricsTable
                                  metrics={hooks.configurations[key]?.metrics}
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Actions */}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Tooltip title={'Descargar listado de ordenes fuera de zona'}>
                  <IconButton onClick={hooks.onDownloadPlan} color="error">
                    <CloudDownload />
                  </IconButton>
                </Tooltip>

                <LoadingButton
                  disableElevation
                  color="secondary"
                  variant="contained"
                  loading={hooks.preloadPlan}
                  disabled={hooks.preloadPlan || !hooks.isValidForm}
                  onClick={hooks.onTryValidatePlans}
                >
                  Preparar planes
                </LoadingButton>
                <LoadingButton
                  variant="contained"
                  disableElevation
                  disabled={
                    hooks.preloadPlan ||
                    !hooks.isValidForm ||
                    !hooks.isValidForCalculate
                  }
                  onClick={hooks.onTrySimulate}
                >
                  Iniciar Simulación
                </LoadingButton>
              </Stack>
            </Stack>
          </Paper>
        </Grid>
        </Grid>
      </Container>
    </>
  );
};